import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NgxDatatableModule, ColumnMode } from '@swimlane/ngx-datatable';
import { Router } from '@angular/router';
import { Page, Pagination } from '../../../../shared/models/page';
import {
  Consultant,
  ConsultantFilters,
  SyncResult,
} from '../../../../shared/models/consultant';
import { ConsultantsService } from '../../../../core/services/consultants.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  Subject,
  switchMap,
} from 'rxjs';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { AlertType } from '../../../../shared/models/alertType';
import { showAlertDialog } from '../../../../shared/helpers/showAlertDialog';
import {
  LoadingService,
  LocalLoadingState,
} from '../../../../core/services/loading.service';
import { CapitalizeWordsPipe } from '../../../../shared/pipes/capitalize-words-pipe';
import { BadgeComponent } from '../../../../shared/components/badge/badge.component';

@Component({
  selector: 'app-consultores-lista',
  imports: [
    MatIconModule,
    CommonModule,
    NgxDatatableModule,
    MatTooltipModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    NgxPermissionsModule,
    CapitalizeWordsPipe,
    BadgeComponent,
  ],
  templateUrl: './consultores-lista.component.html',
  styleUrl: './consultores-lista.component.scss',
})
export class ConsultoresListaComponent implements OnInit, OnDestroy {
  private loadPageSubject = new Subject<ConsultantFilters>();
  private loader: LocalLoadingState;
  consultants: Consultant[] = [];
  columns = [
    { prop: 'nome', name: 'Consultor' },
    { prop: 'email', name: 'E-mail' },
    { name: 'Roteiros Atribuídos' },
    { name: 'Rota Atual' },
    { name: 'Em Atividade?' },
    { name: 'Ações' },
  ];
  page: Page = { pageSize: 5, page: 0, count: 0 };
  ColumnMode = ColumnMode;

  sort: string = 'normal';
  searchControl = new FormControl('');
  canSync: boolean = false;
  isSyncing: boolean = false;

  get isLoading(): boolean {
    return this.loader.isLoading();
  }

  constructor(
    private consultantsService: ConsultantsService,
    private dialog: MatDialog,
    private permissionsService: NgxPermissionsService,
    private loadingService: LoadingService,
    private router: Router
  ) {
    this.loader = this.loadingService.createLocalLoader();
  }

  ngOnInit() {
    this.loadPageSubject
      .pipe(
        switchMap((filters: ConsultantFilters) => {
          this.loader.show();
          return this.consultantsService.getList(filters).pipe(
            finalize(() => this.loader.hide())
          );
        })
      )
      .subscribe((pagedData: Pagination<Consultant>) => {
        this.page.count = pagedData.totalCount;
        this.consultants = pagedData.items;
      });

    this.loadPage();

    this.searchControl.valueChanges
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        filter(() => !this.isLoading)
      )
      .subscribe(() => {
        this.page.page = 0;
        this.loadPage();
      });

    this.permissionsService
      .hasPermission('Permissions.Consultants.Sync')
      .then((hasPermission) => {
        this.canSync = hasPermission;
      });
  }

  onSearchKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.page.page = 0;
      this.loadPage();
    }
  }

  onPage(event: { offset?: number; page?: number }) {
    if (event.page !== undefined) {
      this.page.page = event.page - 1;
    } else if (event.offset !== undefined) {
      this.page.page = event.offset;
    }
    this.loadPage();
  }

  onSort(event: { sorts: { prop: string; dir: string }[] }) {
    const sort = event.sorts[0];
    this.sort = sort.dir === 'asc' ? 'normal' : 'reverse';
    this.page.page = 0;
    this.loadPage();
  }

  loadPage() {
    const filters: ConsultantFilters = {
      sort: this.sort,
      page: this.page.page + 1,
      pageSize: this.page.pageSize,
      search:
        this.searchControl.value != null &&
        this.searchControl.value.trim() !== ''
          ? this.searchControl.value
          : undefined,
    };
    this.loadPageSubject.next(filters);
  }

  syncConsultants() {
    if (this.isSyncing) return;

    const alertDialogRef = showAlertDialog(
      this.dialog,
      AlertType.Warning,
      'Sincronizar Consultores',
      'Deseja sincronizar os consultores com o Omie? Esta operação pode levar alguns segundos.',
      'Sincronizar'
    );

    alertDialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.isSyncing = true;
        this.consultantsService.syncConsultants().subscribe({
          next: (syncResult: SyncResult) => {
            this.isSyncing = false;
            this.page.page = 0;
            this.loadPage();

            const lines = [
              'Consultores adicionados: ' + syncResult.added,
              'Consultores atualizados: ' + syncResult.updated,
              'Consultores desativados: ' + syncResult.deactivated,
            ];

            const hasErrors = syncResult.errors && syncResult.errors.length > 0;
            const errorList = hasErrors
              ? syncResult.errors.map(e => ({
                  email: e.email || e.razaoSocial,
                  reason: e.reason
                }))
              : [];

            let message = lines.join('<br>');
            if (hasErrors) {
              message += '<br><br><strong>Consultores não sincronizados:</strong>';
            }

            const title = hasErrors ? 'Sincronização Parcial' : 'Sucesso!';
            const alertType = hasErrors ? AlertType.PartialSuccess : AlertType.Success;

            showAlertDialog(
              this.dialog,
              alertType,
              title,
              message,
              undefined,
              undefined,
              hasErrors,
              errorList,
              false, // deleteAction
              undefined, // traceId
              true, // isHtml — message usa <br>/<strong> (conteúdo estático controlado)
            );
          },
          error: () => {
            this.isSyncing = false;
          },
        });
      }
    });
  }

  formatDate(dateString: string | Date | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
  }

  formatDateShort(dateString: string | Date | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return format(date, 'MMM yyyy', { locale: ptBR });
  }

  getAssignedRoutesLabel(consultant: Consultant): string {
    const count = consultant.assignedRoutesCount ?? 0;
    return count === 1 ? '1 Roteiro' : `${count} Roteiros`;
  }

  getCurrentRoute(consultant: Consultant): string {
    return consultant.currentRoute?.trim() || '-';
  }

  getStartIndex(): number {
    if (this.page.count === 0) return 0;
    return this.page.page * this.page.pageSize + 1;
  }

  getEndIndex(): number {
    const end = (this.page.page + 1) * this.page.pageSize;
    return Math.min(end, this.page.count);
  }

  ngOnDestroy(): void {
    this.loader.destroy();
  }

  navigateToDetail(id: number): void {
    this.router.navigate(['/consultores/detalhes', id]);
  }
}
